import Text from '@/components/basic/text';
import theme from '@/style';
import React, {useEffect} from 'react';
import {BasicObject, NavigatorScreenProps, SVGProps} from '@/types';
import globalStore from '@/services/global.state';
import {useTranslation} from 'react-i18next';
import {
  BottomTabNavigationOptions,
  createBottomTabNavigator,
} from '@react-navigation/bottom-tabs';
import Home from './pages/home';
import {NativeTouchableOpacity} from '@/components/basic/touchable-opacity';
import {Shadow} from 'react-native-shadow-2';
import Me from './pages/me';
import {HomeSvg, ResultSvg, MeSvg, PromotionsSvg} from '@components/svgs';
import {ReactComponent as ReferralSvg} from '@/assets/icons/main-nav/referral.svg';
import Result from './common-pages/result';
import Invitation from './common-pages/invitation';
import {goTo} from './utils';
// import Scratch from './pages/new-scratch';
import Promotion from '@/pages/promotions/promotion';
import {runWhenBrowserIdle} from '@/utils/defer';
const imgRewardBg = require('@/assets/icons/main-nav/tab-center.png');

const Tab = createBottomTabNavigator();
const loadSportsContainer = () => import('./common-pages/sports/container');

const shadow = {
  startColor: 'rgba(0,0,0,0.05)',
  distance: 4,
  offset: [-1, 0],
  style: [
    theme.flex.row,
    theme.flex.around,
    theme.background.white,
    theme.fill.fillW,
    {
      height: 50,
    },
  ] as BasicObject[],
} as BasicObject;

if (globalStore.isWeb) {
  shadow.distance = 0;
  delete shadow.offset;
  shadow.style.push({
    boxShadow: '0 -1px 4px 0px rgba(0,0,0,0.05)',
  });
}

export const mainPageList: {
  name: string;
  link: string;
  component: (props: NavigatorScreenProps) => React.JSX.Element;
  icon: ({color}: SVGProps) => React.JSX.Element;
  unmountOnBlur?: boolean;
}[] = [
  {
    name: 'Home',
    link: 'index/home',
    component: Home,
    icon: HomeSvg,
  },
  {
    name: 'Promotions',
    link: 'index/promotion',
    component: Promotion,
    icon: PromotionsSvg,
  },
  {
    name: 'Invite',
    link: 'index/referral',
    component: Invitation,
    icon: ({color}) => <ReferralSvg style={{color}} />,
  },
  // {
  //   name: 'ScratchNew',
  //   link: 'index/scratch',
  //   component: Scratch,
  //   icon: () => <></>,
  // },
  {
    name: 'Result',
    link: 'index/result',
    component: Result,
    icon: ResultSvg,
  },
  {
    name: 'Me',
    link: 'index/me',
    component: Me,
    icon: MeSvg,
  },
];

const CusDefautTab = ({
  index,
  label,
  name,
  isFocused,
  options,
  onPress,
}: {
  index: number;
  label: string;
  name: string;
  isFocused: boolean;
  options: BottomTabNavigationOptions;
  onPress: (isFocused: boolean) => void;
}) => {
  const Icon = mainPageList[index].icon;
  const Color = isFocused ? theme.backgroundColor.main : theme.fontColor.accent;
  const renderCenter = () => {
    return (
      <div className="flex flex-col items-center gap-1 cursor-pointer flex-1 relative">
        <div className="absolute left-1/2 bottom-[20px] transform -translate-x-1/2">
          <div className="w-[64px] h-[64px] bg-white rounded-full flex items-center justify-center">
            <div className="w-[50px] h-[50px] bg-[#ea3e41] rounded-full shadow-[0px_3.505px_10.514px_0px_rgba(234,62,99,0.5)] cursor-pointer flex items-center justify-center">
              <div
                className="inset-0 bg-cover bg-center bg-no-repeat w-[44px] h-[44px] transform scale-150"
                style={{backgroundImage: `url('${imgRewardBg}')`}}
              />
            </div>
          </div>
        </div>
        <div className="w-[24px] h-[24px]" />
        <div
          className={`text-[10px] font-bold tracking-[-0.5714px] text-center  ${
            isFocused ? 'text-c1' : 'text-t3'
          }`}>
          Get ₹200
        </div>
      </div>
    );
  };
  return (
    <NativeTouchableOpacity
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityState={isFocused ? {selected: true} : {}}
      accessibilityLabel={options.tabBarAccessibilityLabel}
      testID={options.tabBarTestID}
      onPress={() => onPress(isFocused)}
      style={[
        theme.flex.center,
        theme.flex.flex1,
        theme.position.rel,
        theme.overflow.visible,
      ]}>
      {name === 'ScratchNew' ? renderCenter() : <Icon color={Color} />}

      {name !== 'ScratchNew' && (
        <Text
          blod
          fontSize={10}
          style={{
            color: isFocused
              ? theme.backgroundColor.main
              : theme.fontColor.accent,
          }}>
          {label}
        </Text>
      )}
    </NativeTouchableOpacity>
  );
};

const MainNav = () => {
  const {i18n} = useTranslation();

  useEffect(() => {
    runWhenBrowserIdle(() => {
      loadSportsContainer().catch(() => {});
    }, 3000);
  }, []);

  return (
    <Tab.Navigator
      // eslint-disable-next-line react/no-unstable-nested-components
      tabBar={props => {
        return (
          <Shadow {...shadow}>
            {props.state?.routes.map((route, index) => {
              const {options} = props.descriptors[route.key];
              const label =
                options.tabBarLabel !== undefined
                  ? (options.tabBarLabel as string)
                  : options.title !== undefined
                  ? options.title
                  : route.name;
              const onPress = (isFocused: boolean) => {
                if (isFocused) {
                  return;
                }
                if (route.name === 'Invite' && !globalStore.token) {
                  goTo('Login');
                  return;
                }
                if (route.name === 'Promotions') {
                  goTo('PromotionsV2', {tabKey: 'promotion'});
                  return;
                }
                props.navigation.navigate(route.name);
              };
              return (
                <CusDefautTab
                  key={label}
                  index={index}
                  label={label}
                  name={route.name}
                  isFocused={props.state.index === index}
                  options={options}
                  onPress={onPress}
                />
              );
            })}
          </Shadow>
        );
      }}
      screenOptions={{
        headerShown: false,
      }}>
      {mainPageList.map(v => (
        <Tab.Screen
          key={v.name}
          name={v.name}
          component={v.component}
          options={{
            title: i18n.t(`home.tab.${v.name.toLocaleLowerCase()}`),
            unmountOnBlur: v.unmountOnBlur,
          }}
        />
      ))}
    </Tab.Navigator>
  );
};

export default MainNav;
